import React from 'react';
import { ReceiptText, ChevronLeft, ChevronRight, Repeat2 } from 'lucide-react';
import PublicBillDialog from './PublicBillDialog';

interface BillHeaderProps {
  title: string;
  recurring?: boolean;
  date: string;
  creator?: string;
  emoji?: string;
  receiptImageUrls?: string[] | null;
  showReceipt: boolean;
  setShowReceipt: React.Dispatch<React.SetStateAction<boolean>>;
  currentImageIndex: number;
  setCurrentImageIndex: React.Dispatch<React.SetStateAction<number>>;
  notes?: string;
}

export default function BillMainDetails({ title, recurring = false, date, creator, emoji, receiptImageUrls: storedReceiptUrls, showReceipt, setShowReceipt, currentImageIndex, setCurrentImageIndex, notes }: BillHeaderProps) {
  // Manually entered bills persist null; a destructuring default only handles undefined.
  const receiptImageUrls = storedReceiptUrls ?? [];
  const [failedThumbnail, setFailedThumbnail] = React.useState<string>();
  const firstReceipt = receiptImageUrls[0];
  // Date-only bills describe a calendar day, not midnight UTC in the viewer's zone.
  const parsedDate = new Date(/^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T12:00:00` : date);
  const dateLabel = Number.isNaN(parsedDate.getTime()) ? date : parsedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return (
    <>
      <div className={`bill-heading-row ${firstReceipt ? 'bill-heading-row--with-receipt' : ''}`}>
        {!firstReceipt && <div className="bill-occasion" aria-hidden="true">{emoji || <ReceiptText size={28} strokeWidth={1.6} />}</div>}
        <div>
          <h1 className="bill-overview__title">{title}</h1>
          <p className="bill-meta"><time dateTime={date}>{dateLabel}</time>{creator && <> · Added by {creator}</>}{recurring && <span className="bill-recurring"><Repeat2 size={12} aria-hidden="true" />Recurring</span>}</p>
        </div>
        {firstReceipt && (
          <button type="button" className="bill-receipt-thumbnail" aria-label="View receipt" onClick={() => { setCurrentImageIndex(0); setShowReceipt(true); }}>
            <span className="bill-receipt-thumbnail__image">
              {failedThumbnail === firstReceipt ? <ReceiptText size={28} aria-hidden="true" /> : <img src={firstReceipt} alt="" onError={() => setFailedThumbnail(firstReceipt)} />}
              {receiptImageUrls.length > 1 && <span className="bill-receipt-thumbnail__count" aria-label={`${receiptImageUrls.length} receipt images`}>{receiptImageUrls.length}</span>}
            </span>
          </button>
        )}
      </div>
      {notes && <p className="bill-notes">{notes}</p>}
      <PublicBillDialog open={showReceipt} onClose={() => setShowReceipt(false)} title="Receipt" className="bill-receipt-dialog">
        {receiptImageUrls.length > 0 && <img className="bill-receipt-image" src={receiptImageUrls[currentImageIndex]} alt={`Receipt ${currentImageIndex + 1} of ${receiptImageUrls.length}`} />}
        {receiptImageUrls.length > 1 && <div className="bill-receipt-navigation">
          <button type="button" className="vs-button vs-button--secondary" aria-label="Previous receipt" disabled={currentImageIndex === 0} onClick={() => setCurrentImageIndex((index) => Math.max(0, index - 1))}><ChevronLeft size={18} /> Previous</button>
          <span role="status">{currentImageIndex + 1} / {receiptImageUrls.length}</span>
          <button type="button" className="vs-button vs-button--secondary" aria-label="Next receipt" disabled={currentImageIndex >= receiptImageUrls.length - 1} onClick={() => setCurrentImageIndex((index) => Math.min(receiptImageUrls.length - 1, index + 1))}>Next <ChevronRight size={18} /></button>
        </div>}
      </PublicBillDialog>
    </>
  );
}
